import React from "react";
import { PrimaryButton, SecondaryButton } from "../../../UI";
import { Field, InfoTip, KeyValueEditor, NumberInput, optionsTip, Section, SelectInput, Stat, StatusMessage, TextInput, useStatus } from "../common";
import { PAGINATION_FIELD_HELP, PAGINATION_FIELDS } from "../constants";
import { GLOSSARY } from "../glossary";
import { formatBytes, formatDuration, setIn } from "../helpers";
import QueryParamsEditor from "./QueryParamsEditor";
import { parseQueryRows } from "./urlQuery";

const PAGINATION_TIP = optionsTip(GLOSSARY.request.pagination, GLOSSARY.request.paginationTypes);
const ON_ERROR_TIP = optionsTip("What happens when one of the dependent calls fails.", GLOSSARY.request.onError, { fail: "Fail the source", skip: "Skip that value" });

const SECRET_MASK = "********";

const NUMBER_FIELDS = ["start", "size", "limit", "start_offset", "max_pages"];

// Step 1: the request.
const RequestStep = ({ source, update, sources, meta, testResult, testing, runTest, onNext }) => {
    const request = source.request || {};
    const setRequest = (path, value) => update((s) => setIn(s, ["request", ...path], value));
    const pagination = request.pagination || { type: "none" };
    const forEach = request.for_each;
    const otherSources = sources.filter((s) => s.id !== source.id);
    const [status, showStatus] = useStatus(10000);
    const secretParams = (request.query || []).filter((entry) => entry.secret);
    const paramCount = parseQueryRows(request.url || "").length + secretParams.length;

    // URL and params table are two views of the same query string.
    const setUrlAndQuery = ({ url, query }) => update((s) => ({ ...s, request: { ...s.request, url, query } }));

    const test = async () => {
        showStatus("Calling the API...", "info");
        const { data, error } = await runTest();
        if (error) return showStatus(`Request failed: ${error}`, "error");
        const top = data?.detection?.suggestions?.[0];
        showStatus(
            `Fetched ${data?.stats?.documents || 0} document(s), ${formatBytes(data?.stats?.bytes)}.` +
            (top ? ` Suggested structure: ${top.label} (${top.counts.products} products, ${top.counts.variants} variants).` : " No product list was detected.")
        );
    };

    return (
        <div className="flex flex-col gap-4">
            <Section title="Request" description="The URL is sent exactly as entered. Placeholders: {{repeat}} (repeat values), {{page}}, {{offset}}, {{cursor}} (pagination) and {{each}} (values from another source).">
                <div className="grid grid-cols-1 md:grid-cols-[120px_1fr_140px] gap-3">
                    <Field label="Method" tip={GLOSSARY.request.method}>
                        <SelectInput value={request.method || "GET"} options={["GET", "POST"]} onChange={(v) => setRequest(["method"], v)} />
                    </Field>
                    <Field label="URL" tip={GLOSSARY.request.url}>
                        <TextInput
                            monospace
                            value={request.url}
                            placeholder="https://api.example.com/products?category=phones"
                            onChange={(url) => setUrlAndQuery({ url, query: request.query || [] })}
                        />
                    </Field>
                    <Field label="Format" tip={GLOSSARY.request.format}>
                        <SelectInput value={request.format || "json"} options={meta?.request_formats || ["json", "ndjson"]} onChange={(v) => setRequest(["format"], v)} />
                    </Field>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Field
                        label={`Query parameters${paramCount ? ` (${paramCount})` : ""}`}
                        help="Edits here update the URL and vice versa. Values are sent as typed; &, # and spaces are encoded for you."
                        tip={GLOSSARY.request.query}
                    >
                        <QueryParamsEditor url={request.url || ""} secrets={secretParams} secretMask={SECRET_MASK} onChange={setUrlAndQuery} />
                    </Field>
                    <Field label="Headers" help="Mark API keys as secret: they're stored encrypted and never shown again." tip={GLOSSARY.request.headers}>
                        <KeyValueEditor items={request.headers || []} allowSecret onChange={(v) => setRequest(["headers"], v)} />
                    </Field>
                </div>
                {request.method === "POST" && (
                    <Field label="Body (JSON)" help='In the body, placeholders become JSON values, e.g. {"skus": {{each}}}. Put secrets in headers.' tip={GLOSSARY.request.body}>
                        <textarea
                            className="block px-2.5 py-2 w-full text-xs font-mono text-gray-900 bg-white rounded-lg border border-gray-2 focus:outline-none focus:border-primary"
                            rows={5}
                            value={typeof request.body === "string" ? request.body : request.body ? JSON.stringify(request.body, null, 2) : ""}
                            onChange={(e) => setRequest(["body"], e.target.value)}
                        />
                    </Field>
                )}
                <Field label="Repeat for" help="One request per value, e.g. categories or locales. Use {{repeat}} in the URL or query. One value per line." tip={GLOSSARY.request.repeatFor}>
                    <textarea
                        className="block px-2.5 py-2 w-full text-sm font-mono text-gray-900 bg-white rounded-lg border border-gray-2 focus:outline-none focus:border-primary"
                        rows={2}
                        value={(request.repeat_for || []).join("\n")}
                        onChange={(e) => setRequest(["repeat_for"], e.target.value.split("\n").map((v) => v.trim()).filter(Boolean))}
                    />
                </Field>
            </Section>

            <Section title="Pagination" tip={GLOSSARY.request.pagination}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Type" tip={PAGINATION_TIP}>
                        <SelectInput value={pagination.type || "none"} options={meta?.pagination_types || Object.keys(PAGINATION_FIELDS)} onChange={(v) => setRequest(["pagination"], { type: v })} />
                    </Field>
                    {(PAGINATION_FIELDS[pagination.type || "none"] || []).map((key) =>
                        NUMBER_FIELDS.includes(key) ? (
                            <Field key={key} label={PAGINATION_FIELD_HELP[key]}>
                                <NumberInput value={pagination[key]} min={0} onChange={(v) => setRequest(["pagination", key], v)} />
                            </Field>
                        ) : (
                            <Field key={key} label={PAGINATION_FIELD_HELP[key]}>
                                <TextInput monospace value={pagination[key]} placeholder={key.endsWith("_path") ? "$.pageInfo.endCursor" : ""} onChange={(v) => setRequest(["pagination", key], v || undefined)} />
                            </Field>
                        )
                    )}
                </div>
            </Section>

            <Section
                title="Dependent calls"
                description="Call this API once per value found in another source, e.g. a detail call per product id."
                tip={GLOSSARY.request.dependent}
                actions={
                    forEach ? (
                        <SecondaryButton size="xs" handleClick={() => setRequest(["for_each"], undefined)}>Remove</SecondaryButton>
                    ) : (
                        <SecondaryButton size="xs" disable={!otherSources.length} handleClick={() => setRequest(["for_each"], { source: otherSources[0]?.id, value: "$product.id", batch_size: 1, concurrency: 4, on_error: "skip" })}>
                            Add
                        </SecondaryButton>
                    )
                }
            >
                {forEach && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Field label="Values from source" tip={GLOSSARY.request.forEachSource}>
                            <SelectInput value={forEach.source} options={otherSources.map((s) => ({ value: s.id, label: s.name || s.id }))} onChange={(v) => setRequest(["for_each", "source"], v)} />
                        </Field>
                        <Field label="Value" help="$product.<field> or $variant.<field> of that source's mapped records" tip={GLOSSARY.request.forEachValue}>
                            <TextInput monospace value={typeof forEach.value === "string" ? forEach.value : forEach.value?.path} onChange={(v) => setRequest(["for_each", "value"], v)} />
                        </Field>
                        <Field label="Batch size" help="More than 1 sends several values per call" tip={GLOSSARY.request.batchSize}>
                            <NumberInput value={forEach.batch_size} min={1} max={1000} onChange={(v) => setRequest(["for_each", "batch_size"], v)} />
                        </Field>
                        <Field label="Batch separator (URL)" tip={GLOSSARY.request.batchJoin}>
                            <TextInput value={forEach.batch_join} placeholder="," onChange={(v) => setRequest(["for_each", "batch_join"], v || undefined)} />
                        </Field>
                        <Field label="Concurrent calls" tip={GLOSSARY.request.concurrency}>
                            <NumberInput value={forEach.concurrency} min={1} max={8} onChange={(v) => setRequest(["for_each", "concurrency"], v)} />
                        </Field>
                        <Field label="Maximum calls" tip={GLOSSARY.request.maxCalls}>
                            <NumberInput value={forEach.max_calls} min={1} max={5000} onChange={(v) => setRequest(["for_each", "max_calls"], v)} />
                        </Field>
                        <Field label="When a call fails" help="Skipping suits detail calls, where a single page can be missing" tip={ON_ERROR_TIP}>
                            <SelectInput
                                value={forEach.on_error || "fail"}
                                options={[
                                    { value: "fail", label: "Fail the source" },
                                    { value: "skip", label: "Skip that value" },
                                ]}
                                onChange={(v) => setRequest(["for_each", "on_error"], v === "fail" ? undefined : v)}
                            />
                        </Field>
                        {forEach.on_error === "skip" && (
                            <Field label="Failed calls allowed (%)" help="Above this share the source fails" tip={GLOSSARY.request.maxFailedPct}>
                                <NumberInput value={forEach.max_failed_pct} placeholder="10" min={0} max={100} onChange={(v) => setRequest(["for_each", "max_failed_pct"], v)} />
                            </Field>
                        )}
                    </div>
                )}
            </Section>

            <Section title="Limits">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <Field label="Timeout (ms)" tip={GLOSSARY.request.timeout}>
                        <NumberInput value={request.timeout_ms} placeholder="30000" onChange={(v) => setRequest(["timeout_ms"], v)} />
                    </Field>
                    <Field label="Max response size (bytes)" tip={GLOSSARY.request.maxBytes}>
                        <NumberInput value={request.max_bytes} placeholder="26214400" onChange={(v) => setRequest(["max_bytes"], v)} />
                    </Field>
                    <Field label="Retries" tip={GLOSSARY.request.retries}>
                        <NumberInput value={request.retry?.attempts} placeholder="2" min={0} max={5} onChange={(v) => setRequest(["retry", "attempts"], v)} />
                    </Field>
                    <Field label="Retry backoff (ms)" tip={GLOSSARY.request.backoff}>
                        <NumberInput value={request.retry?.backoff_ms} placeholder="1000" onChange={(v) => setRequest(["retry", "backoff_ms"], v)} />
                    </Field>
                </div>
            </Section>

            <div className="flex flex-wrap items-center gap-3">
                <PrimaryButton size="sm" loading={testing} disable={!request.url} handleClick={test}>Test request</PrimaryButton>
                <InfoTip content={GLOSSARY.request.test} />
                {testResult && <SecondaryButton size="sm" disable={testing} handleClick={onNext}>Next: structure</SecondaryButton>}
                <StatusMessage status={status} />
            </div>

            {testResult && (
                <Section title="Test result">
                    <div className="flex flex-wrap gap-2">
                        <Stat label="Documents" value={testResult.stats?.documents} tip={GLOSSARY.stats.documents} />
                        <Stat label="Calls" value={testResult.stats?.calls} tip={GLOSSARY.stats.calls} />
                        <Stat label="Size" value={formatBytes(testResult.stats?.bytes)} />
                        <Stat label="Top suggestion" value={testResult.detection?.suggestions?.[0]?.label} tip={GLOSSARY.structure.suggestions} />
                    </div>
                    <table className="text-xs w-full">
                        <thead>
                            <tr className="text-left text-gray-500"><th className="py-1">#</th><th>URL</th><th>Status</th><th>Size</th><th>Time</th></tr>
                        </thead>
                        <tbody>
                            {(testResult.documents || []).map((doc, i) => (
                                <tr key={i} className="border-t border-gray-100">
                                    <td className="py-1">{i + 1}</td>
                                    <td className="font-mono break-all">{doc.url}</td>
                                    <td>{doc.status}</td>
                                    <td>{formatBytes(doc.bytes)}</td>
                                    <td>{formatDuration(doc.ms)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {testResult.stats?.truncated && <p className="text-xs text-gray-500">Only the first documents were fetched for the test.</p>}
                </Section>
            )}
        </div>
    );
};

export default RequestStep;
